import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, Flex, Icon, Input, Text, Tooltip } from '@chakra-ui/react';
import { CustomInfoOutlineIcon } from '../../../../theme/icons/CustomInfoOutlineIcon';
import { CustomErrorIcon } from '../../../../theme/icons/CustomErrorIcon';
import { Link } from 'react-router-dom';
import SectionWrapper from '../../pass-templates-new/SectionWrapper';
import { CustomArrowRightIcon } from '../../../../theme/icons/CustomArrowRightIcon';
import PassesRenderStatus from '../PassesRenderStatus';
import { PASS_TYPE_LABELS, DEVICE_LABELS } from '../../../../store/types/pass';
import type {
  PassAttributes,
  PassDeviceType,
} from '../../../../store/types/pass';
import type { PassTemplateMeta } from '../../../../store/types/pass';
import Select from 'react-select';
import { passBuilderSelectStyles } from '../../../../theme/reactSelectStyles';
import { MeContext } from '../../../../context/MeContext';
import { HttpContext } from '../../../../context/HttpContext';
import type { MeContextState } from '../../../../store/types/account';

type TemplateOption = {
  label: string;
  value: string;
  entityId: string;
  templateType: string;
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box mt="24px" _first={{ mt: 0 }}>
    <Text textStyle="bodyMedium" fontSize="16px">
      {label}
    </Text>
    <Text textStyle="bodyRegular" fontSize="16px" color="#989898" mt="6px">
      {children}
    </Text>
  </Box>
);

const LabelWithTooltip = ({
  label,
  tooltip,
  required,
}: {
  label: string;
  tooltip: string;
  required?: boolean;
}) => (
  <Flex align="center" gap={1}>
    <Text textStyle="bodyMedium" fontSize="16px">
      {label}
      {required && (
        <Text as="span" color="#FF1010" fontWeight="bold" ml="2px">
          *
        </Text>
      )}
    </Text>
    <Tooltip
      label={tooltip}
      hasArrow
      placement="top"
      fontSize="12px"
      bg="inkBlue"
      color="white"
      borderRadius="6px"
      textAlign="center"
      p="10px"
    >
      <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        lineHeight="1"
        position="relative"
        top="-1px"
        cursor="pointer"
      >
        <Icon as={CustomInfoOutlineIcon} fill="grey" boxSize="15px" />
      </Box>
    </Tooltip>
  </Flex>
);

interface PassOverviewSectionProps {
  uuid?: string;
  attributes?: PassAttributes;
  template?: PassTemplateMeta;
  isEdit?: boolean;
  isCreate?: boolean;
  extIdRequired?: boolean;
  errors?: Record<string, string>;
  focusedField?: string | null;
  onSetExtId?: (extId: string) => void;
  onTemplateChange?: (templateUuid: string) => void;
  onFieldFocus?: (identifier: string) => void;
}

const PassOverviewSection = ({
  uuid,
  attributes,
  template,
  isEdit,
  isCreate,
  extIdRequired,
  errors = {},
  focusedField,
  onSetExtId,
  onTemplateChange,
  onFieldFocus,
}: PassOverviewSectionProps) => {
  const [extIdInput, setExtIdInput] = useState('');
  const canSetExtId = isCreate || (isEdit && !attributes?.extId);

  const meCtx = useContext(MeContext) as {
    state: MeContextState & {
      entities?: unknown[];
      filterEntity?: { uuid: string } | null;
    };
    updateFilterEntity: (entity: unknown) => void;
  };
  const { filterEntity } = meCtx.state;
  const { authAxios } = useContext(HttpContext);

  const [selectedCreateOption, setSelectedCreateOption] =
    useState<TemplateOption | null>(null);
  const [templateOptions, setTemplateOptions] = useState<TemplateOption[]>([]);
  const [templatePage, setTemplatePage] = useState(1);
  const [hasMoreTemplates, setHasMoreTemplates] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const searchTermRef = useRef('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const fetchTemplates = useCallback(
    async (page: number, search: string) => {
      const entityPath = filterEntity?.uuid
        ? `api/v1/entities/${filterEntity.uuid}/pass-templates`
        : 'api/v1/pass-templates';
      const params: Record<string, string> = {
        fields: 'name,templateType,entityId',
        skipImages: 'true',
        perPage: '100',
        page: String(page),
      };
      if (search) {
        params.search = search;
      }
      const response = await authAxios.get(entityPath, { params });
      const templates = response.data?.data || [];
      const totalPages = response.data?.meta?.totalPages || 1;
      const options = templates.map(
        (t: {
          id: string;
          attributes: {
            name: string;
            entityId: string;
            templateType: string;
          };
        }) => ({
          label: t.attributes.name,
          value: t.id,
          entityId: t.attributes.entityId,
          templateType: t.attributes.templateType,
        })
      );
      return { options, hasMore: page < totalPages };
    },
    [authAxios, filterEntity?.uuid]
  );

  const loadInitialOptions = useCallback(async () => {
    try {
      searchTermRef.current = '';
      const { options, hasMore } = await fetchTemplates(1, '');
      setTemplateOptions(options);
      setTemplatePage(1);
      setHasMoreTemplates(hasMore);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  }, [fetchTemplates]);

  useEffect(() => {
    if (isCreate) {
      loadInitialOptions();
    }
  }, [isCreate, loadInitialOptions]);

  const handleInputChange = (inputValue: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        searchTermRef.current = inputValue;
        setIsLoadingSearch(true);
        const { options, hasMore } = await fetchTemplates(1, inputValue);
        setTemplateOptions(options);
        setTemplatePage(1);
        setHasMoreTemplates(hasMore);
      } catch (error) {
        console.error('Error searching templates:', error);
      } finally {
        setIsLoadingSearch(false);
      }
    }, 300);
  };

  const loadMoreTemplates = async () => {
    if (!hasMoreTemplates || isLoadingMore) return;
    try {
      setIsLoadingMore(true);
      const nextPage = templatePage + 1;
      const { options, hasMore } = await fetchTemplates(
        nextPage,
        searchTermRef.current
      );
      setTemplateOptions((prev) => [...prev, ...options]);
      setTemplatePage(nextPage);
      setHasMoreTemplates(hasMore);
    } catch (error) {
      console.error('Error loading more templates:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const entityUuid = filterEntity?.uuid;
  const isFirstRender = useRef(true);
  const entitySetByTemplateRef = useRef(false);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (entitySetByTemplateRef.current) {
      entitySetByTemplateRef.current = false;
      return;
    }
    if (!isCreate) return;
    setSelectedCreateOption(null);
    onTemplateChange?.('');
  }, [entityUuid]);

  const applyTemplateSelection = (option: TemplateOption | null) => {
    setSelectedCreateOption(option);
    onTemplateChange?.(option?.value ?? '');
    if (option?.entityId && meCtx.state.entities) {
      const entity = (meCtx.state.entities as { id: string | number }[]).find(
        (e) => String(e.id) === String(option.entityId)
      );
      if (entity) {
        const isEntityChange =
          String((entity as { uuid?: string }).uuid) !== entityUuid;
        if (isEntityChange) {
          entitySetByTemplateRef.current = true;
          meCtx.updateFilterEntity(entity);
        }
      }
    }
  };

  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (!isCreate || !meCtx.state.firstPassImprovementsEnabled) return;
    if (autoSelectedRef.current || selectedCreateOption) return;
    if (searchTermRef.current) return;
    if (templateOptions.length !== 1 || hasMoreTemplates) return;
    autoSelectedRef.current = true;
    applyTemplateSelection(templateOptions[0]);
  }, [
    isCreate,
    meCtx.state.firstPassImprovementsEnabled,
    templateOptions,
    hasMoreTemplates,
    selectedCreateOption,
  ]);

  const selectedTemplate = template
    ? { label: template.name, value: template.uuid }
    : selectedCreateOption;

  const hasTemplateSelected = isCreate ? !!selectedCreateOption : true;

  return (
    <Box>
      <SectionWrapper
        title={
          <Flex align="center" gap={3}>
            Pass overview
            {attributes?.status && (
              <PassesRenderStatus
                value={
                  attributes.expiresAt &&
                  new Date(attributes.expiresAt) < new Date()
                    ? 'expired'
                    : attributes.status
                }
                hasTooltip
                light
              />
            )}
          </Flex>
        }
        openOnLoad
        id="pass-overview-section"
      >
        <Text textStyle="bodyMedium" fontSize="16px">
          Pass template
        </Text>
        {isCreate ? (
          <Box mt="10px">
            <Select
              key={entityUuid || 'all'}
              options={templateOptions}
              value={selectedTemplate}
              onFocus={() => onFieldFocus?.('templateUuid')}
              onChange={applyTemplateSelection}
              onInputChange={handleInputChange}
              onMenuScrollToBottom={loadMoreTemplates}
              isLoading={isLoadingMore || isLoadingSearch}
              filterOption={null}
              noOptionsMessage={({ inputValue }) =>
                inputValue
                  ? 'No templates match your search'
                  : 'No templates available — only published templates can issue passes'
              }
              placeholder="Select a template"
              isSearchable
              styles={passBuilderSelectStyles}
            />
            {errors.templateUuid && (
              <Flex align="center" gap={1.5} pl={2} mt={2}>
                <CustomErrorIcon boxSize="12px" color="#E53E3E" />
                <Text fontSize="12px" color="#E53E3E">
                  {errors.templateUuid}
                </Text>
              </Flex>
            )}
          </Box>
        ) : (
          <Flex mt="6px" gap={1}>
            <Text textStyle="bodyRegular" fontSize="16px" color="#989898">
              Issued from
            </Text>
            <Link to={`/pass-templates/edit/${template?.uuid}`}>
              <Flex gap={1} alignItems="center">
                <Text textStyle="bodyRegular" fontSize="16px" color="#009BD6">
                  {template?.name}
                </Text>
                <CustomArrowRightIcon boxSize="11px" color="#009BD6" />
              </Flex>
            </Link>
          </Flex>
        )}

        {hasTemplateSelected && (
          <Field label="Pass type">
            {isCreate && selectedCreateOption?.templateType
              ? (PASS_TYPE_LABELS[selectedCreateOption.templateType] ??
                selectedCreateOption.templateType)
              : (PASS_TYPE_LABELS[attributes?.passType ?? ''] ??
                attributes?.passType)}
          </Field>
        )}

        {hasTemplateSelected && canSetExtId ? (
          <Box mt="24px">
            <LabelWithTooltip
              label="Ext ID"
              tooltip="Links this pass to a record in your external system or CRM. Once set, it cannot be changed."
              required={extIdRequired}
            />
            <Flex mt="6px" gap={2} align="center">
              <Input
                size="sm"
                borderRadius="5px"
                placeholder="Enter external ID"
                value={extIdInput}
                fontSize="13px"
                onFocus={() => onFieldFocus?.('extId')}
                onChange={(e) => {
                  setExtIdInput(e.target.value);
                  onSetExtId?.(e.target.value);
                }}
              />
            </Flex>
            {errors.extId && (
              <Flex align="center" gap={1.5} pl={2} mt={2}>
                <CustomErrorIcon boxSize="12px" color="#E53E3E" />
                <Text fontSize="12px" color="#E53E3E">
                  {errors.extId}
                </Text>
              </Flex>
            )}
          </Box>
        ) : hasTemplateSelected ? (
          <Box mt="24px">
            <LabelWithTooltip
              label="Ext ID"
              tooltip="Links this pass to a record in your external system or CRM. Once set, it cannot be changed."
            />
            <Text
              textStyle="bodyRegular"
              fontSize="16px"
              color="#989898"
              mt="6px"
            >
              {attributes?.extId || 'Not set'}
            </Text>
          </Box>
        ) : null}

        {!isCreate && (
          <Box mt="24px">
            <LabelWithTooltip
              label="UUID"
              tooltip="A unique identifier automatically generated by PassEntry when the pass is created. This cannot be changed."
            />
            <Text
              textStyle="bodyRegular"
              fontSize="16px"
              color="#989898"
              mt="6px"
            >
              {uuid}
            </Text>
          </Box>
        )}

        {attributes?.deviceType && (
          <Field label="Device">
            {DEVICE_LABELS[attributes.deviceType as PassDeviceType]}
          </Field>
        )}
      </SectionWrapper>
    </Box>
  );
};

export default PassOverviewSection;
